import { useQuery } from '@apollo/client'
import { ErrorComponent, NotFound, ProgressSpinner } from '@medical/components'
import ClinicNameRender from '@medical/components/ClinicNameRender'
import { CATEGORY_TRANSPORT } from '@medical/constant'
import {
  calcTotalTransportationExpense,
  calcWorkScheduleTotalDays,
  calcWorkScheduleTotalHours,
  calcWorkScheduleTotalSalary,
  clinicalDepartmentNames,
  dateValidation,
} from '@medical/libs'
import { GET_INCENTIVE } from '@medical/pages/staff/Incentive/ListOfIncentives/ListOfIncentives.graphql'
import { useCustom } from '@medical/provider/context'
import Router from '@medical/routes/router'
import moment from 'moment'
import React, { useEffect } from 'react'
import { Redirect } from 'react-router-dom'

import {
  DOCTOR_MONTHLY_INCENTIVE_AMOUNT,
  DOCTOR_WORK_REPORTS,
} from './DoctorWorkReport.graph'
import DoctorWorkReportScene from './DoctorWorkReportScene'

const DoctorWorkReport = ({
  match: {
    path,
    params: { id, year, month = moment().month() + 1 },
  },
}) => {
  const [{ i18n }] = useCustom()
  const date = moment()
    .year(year)
    .month(month - 1)
    .startOf('month')

  const { loading, error, data, refetch } = useQuery(DOCTOR_WORK_REPORTS, {
    variables: {
      where: {
        id,
      },
      date,
      doctorId: id,
    },
    fetchPolicy: 'network-only',
  })

  const { data: dataTotalIncentive } = useQuery(
    DOCTOR_MONTHLY_INCENTIVE_AMOUNT,
    {
      variables: {
        date: new Date(date.format('YYYY-MM-DD')),
        doctorId: id,
      },
      fetchPolicy: 'network-only',
    }
  )
  const incentiveTypes = [
    'NORMAL_INCENTIVE',
    'NUMBER_INCENTIVE',
    'BEYOND_EIGHT_INCENTIVE',
    'TIME_INCENTIVE',
    'SATISFACTION_INCENTIVE',
  ]
  const incentiveTypeSearch = () => {
    if (
      moment()
        .subtract(2, 'month')
        .isSameOrAfter(date)
    ) {
      return incentiveTypes
    }
    if (
      moment()
        .subtract(1, 'month')
        .year() === date.year() &&
      moment()
        .subtract(1, 'month')
        .month() === date.month()
    ) {
      if (moment().date() >= 15) {
        return incentiveTypes
      }
      return ['NORMAL_INCENTIVE']
    }
    return ['NORMAL_INCENTIVE']
  }

  const variables = {
    where: {
      incentiveTotalAmount_gte: 1,
      applyTime: moment()
        .utc()
        .year(year)
        .month(month - 1)
        .startOf('month'),
      Doctor: {
        id,
        deletedAt: null,
      },
      Incentive: {
        type_in: incentiveTypeSearch(),
      },
    },
  }

  const { data: dataIncentives } = useQuery(GET_INCENTIVE, {
    variables,
    fetchPolicy: 'network-only',
  })

  useEffect(() => {
    refetch()
  })
  if (
    dateValidation({ year, month }, true) ||
    [
      Router.staffDoctorWorkReportWithoutMonth,
      Router.staffDoctorWorkReportWithoutYearMonth,
    ].includes(path)
  )
    return (
      <Redirect
        to={Router.get(Router.staffDoctorWorkReport, {
          id,
          year: moment().year(),
          month: moment().month() + 1,
        })}
      />
    )
  if (loading) return <ProgressSpinner />
  if (error) return <ErrorComponent error={error} />
  if (!data.doctor) {
    return <NotFound />
  }
  const { doctor } = data

  const { doctorMonthlyWorkSchedules: workSchedules = [] } = data.doctor || {}

  const { doctorMonthlyTransportationExpenses: transportationExpenses = [] } =
    data.doctor || {}

  const departmentNames = clinicalDepartmentNames({
    departments: data.departments,
  })

  const sortedWorkSchedules = workSchedules
    .sort((a, b) => moment(a.startTime) - moment(b.startTime))
    .map(ws => ({
      id: ws.id,
      deletedAt: ws.deletedAt,
      clinic: ClinicNameRender({
        clinicalDepartment: ws.clinicalDepartment,
      }),
      clinicKana: ws.clinicalDepartment.clinic.nameKana,
      clinicalDepartment: departmentNames[ws.clinicalDepartment.name],
      startTime: ws.startTime,
      endTime: ws.endTime,
      dailySalary: ws.acceptedShift.dailySalary,
      costWs: ws.isTypeTransportation
        ? ws.costTransportationByMyCar
        : ws.costTransportationByDoctorWorkSchedule.length > 0
        ? ws.costTransportationByDoctorWorkSchedule[0].cost
        : 0,
      costTransportationByDoctorWorkSchedule: ws.isTypeTransportation
        ? ws.costTransportationByMyCar
        : ws.costTransportationByDoctorWorkSchedule.length > 0
        ? ws.costTransportationByDoctorWorkSchedule[0].returnPath[2]
        : 0,
      notApplyCostTransportation: ws.notApplyCostTransportation,
    }))

  const totalHours = calcWorkScheduleTotalHours(sortedWorkSchedules)

  const totalSalary = calcWorkScheduleTotalSalary(sortedWorkSchedules)

  const totalDays = calcWorkScheduleTotalDays(sortedWorkSchedules)
  const lstTransportationWithDate = []
  for (const transportation of transportationExpenses) {
    if (transportation.category === CATEGORY_TRANSPORT.TRANFFIC_FEE) {
      lstTransportationWithDate.push({ ...transportation })
    } else {
      const startTime = moment.utc(transportation.startTime).clone()
      const endTime = moment.utc(transportation.endTime)
      while (startTime.isSameOrBefore(endTime)) {
        const timeCurrent = moment.utc(startTime).format('YYYY-MM-DD')
        const datesCheck = transportation.blockedDays
          ? transportation.blockedDays.split(',')
          : []

        if (!datesCheck.includes(timeCurrent.slice(0, 10))) {
          lstTransportationWithDate.push({
            ...transportation,
            startTime: `${timeCurrent}T00:00:00.000Z`,
            endTime: `${timeCurrent}T00:00:00.000Z`,
          })
        }
        startTime.add(1, 'days')
      }
    }
  }
  const transportationExpensesCost = lstTransportationWithDate.map(item => ({
    cost: item.cost,
    deletedAt: item.deletedAt,
  }))
  const totalTransportationExpense = calcTotalTransportationExpense(
    transportationExpensesCost,
    sortedWorkSchedules
  )
  const getNormalIncentiveTypes = () => {
    const result = []
    if (dataIncentives.incentiveDetailsConnection) {
      dataIncentives.incentiveDetailsConnection.edges.forEach(({ node }) => {
        if (node.incentiveTotalAmount && node.incentiveTotalAmount > 0) {
          if (node.incentive.type === 'NORMAL_INCENTIVE') {
            result.push({
              type: node.incentive.title,
              value: node.incentiveTotalAmount,
            })
          }
        }
      })
    }
    return result
  }

  const getOtherIncentiveTypes = () => {
    if (dataIncentives.incentiveDetailsConnection) {
      const otherIncentiveTypes = []
      dataIncentives.incentiveDetailsConnection.edges.forEach(({ node }) => {
        if (node.incentiveTotalAmount && node.incentiveTotalAmount > 0) {
          if (node.incentive.type !== 'NORMAL_INCENTIVE') {
            otherIncentiveTypes.push({
              type: node.incentive.type,
              value: node.incentiveTotalAmount,
            })
          }
        }
        return otherIncentiveTypes
      })
      return otherIncentiveTypes.reduce((finalObj, cur) => {
        if (cur.type in finalObj) {
          // eslint-disable-next-line operator-assignment
          finalObj[cur.type] = finalObj[cur.type] + cur.value
        } else {
          finalObj[cur.type] = cur.value
        }
        return finalObj
      }, {})
    }
  }

  return (
    <DoctorWorkReportScene
      i18n={i18n}
      doctorId={id}
      doctor={doctor}
      date={date}
      workSchedules={sortedWorkSchedules}
      totalHours={totalHours}
      totalDays={totalDays}
      totalSalary={totalSalary}
      totalTransportationExpense={totalTransportationExpense}
      totalIncentive={dataTotalIncentive.doctorMonthlyIncentive}
      normalIncentiveTypes={getNormalIncentiveTypes()}
      otherIncentive={getOtherIncentiveTypes()}
    />
  )
}

export default DoctorWorkReport
